
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

export type Invitation = {
  id: string;
  email: string;
  status: string;
  progress?: number | null;
  completed?: boolean;
};

export function useResourceInvitations(resourceId: string, isDialogOpen: boolean) {
  const [existingInvites, setExistingInvites] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchExistingInvites = async () => {
    try {
      const {
        data: invitations,
        error: invitationsError
      } = await supabase.from('resource_invitations').select('id, email, status').eq('resource_id', resourceId);
      if (invitationsError) throw invitationsError;
      
      const { data: files, error: filesError } = await supabase
        .from('resource_files')
        .select('id')
        .eq('resource_id', resourceId);
      if (filesError) throw filesError;
      
      const totalItems = files?.length || 0;
      
      const {
        data: shares,
        error: sharesError
      } = await supabase
        .from('resource_shares')
        .select('trainee:trainee_id (email), progress, completed_at, trainee_id')
        .eq('resource_id', resourceId);
      if (sharesError) throw sharesError;
      
      const progressMap = new Map();
      
      if (shares && shares.length > 0) {
        for (const share of shares) {
          if (share.trainee?.email) {
            const isCompleted = share.progress === 100 || !!share.completed_at;
            
            progressMap.set(share.trainee.email, {
              progress: share.progress || 0,
              completed: isCompleted
            });
          }
        }
      }
      
      const combinedInvites = invitations?.map(invite => ({
        ...invite,
        progress: progressMap.get(invite.email)?.progress || 0,
        completed: progressMap.get(invite.email)?.completed || false
      })) || [];
      
      setExistingInvites(combinedInvites);
    } catch (error) {
      console.error('Error fetching invites:', error);
      toast({
        title: "Error fetching invitations",
        description: "There was a problem loading the invitations. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      const {
        error
      } = await supabase.from('resource_invitations').delete().eq('id', inviteId);
      if (error) throw error;
      await fetchExistingInvites();
      toast({
        title: "Invitation revoked",
        description: "The invitation has been successfully revoked."
      });
    } catch (error) {
      console.error('Error revoking invite:', error);
      toast({
        title: "Error revoking invitation",
        description: "There was a problem revoking the invitation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadCertificate = async (email: string) => {
    try {
      const completionDate = format(new Date(), 'MMMM d, yyyy');
      
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('email', email)
        .maybeSingle();
      
      if (userError) {
        console.error('Error fetching user profile:', userError);
      }
      
      let traineeName = email;
      
      if (userData) {
        if (userData.first_name && userData.last_name) {
          traineeName = `${userData.first_name} ${userData.last_name}`;
        } else if (userData.first_name) {
          traineeName = userData.first_name;
        } else if (userData.last_name) {
          traineeName = userData.last_name;
        }
      }
      
      console.log('Admin certificate download for user:', {
        email: email,
        name: traineeName,
        profile: userData
      });
      
      const { data, error } = await supabase.functions.invoke('generate-certificate', {
        body: {
          resourceId,
          traineeName,
          topic: '', // This will be passed from the parent component
          completionDate
        }
      });
      
      if (error) throw error;
      window.open(data.certificateUrl, '_blank');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: "Error",
        description: "Failed to download certificate. Please try again.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (isDialogOpen) {
      fetchExistingInvites();
    }
  }, [isDialogOpen, resourceId]);

  return {
    existingInvites,
    fetchExistingInvites,
    handleRevokeInvite,
    handleDownloadCertificate,
    isLoading,
    setIsLoading
  };
}
